<script setup lang="ts">
import Autocomplete from '@/components/forms/Autocomplete/Autocomplete.vue';
import SearchField from '@/components/filter/SearchField.vue';
import { JobOccurrenceFilterOptionsDto } from '@/api/generator';
import { JobOccurrencesFilterObject } from '../types';
import { ref, computed, watchEffect, toRefs } from 'vue';
import PtwResetFilterButton from '@/components/buttons/PtwResetFilterButton.vue';
import { useTranslation } from 'i18next-vue';
import { DateTime } from 'luxon';
import DatePicker from '@/components/forms/DatePicker/DatePicker.vue';
import { ActiveTabType } from '@/components/Tabs/types';
import { toUserTime } from '@/components/formatters/utils';

const props = withDefaults(
  defineProps<{
    modelValue: JobOccurrencesFilterObject;
    filterOptions?: JobOccurrenceFilterOptionsDto;
    activeTab?: ActiveTabType;
  }>(),
  {
    filterOptions: undefined,
    activeTab: undefined,
  },
);

const emits = defineEmits<{
  'update:modelValue': [filterObject: JobOccurrencesFilterObject];
}>();

const filterObject = ref<JobOccurrencesFilterObject>(props.modelValue);

watchEffect(() => {
  filterObject.value = props.modelValue;
});

function updateFilterObject() {
  emits('update:modelValue', { ...filterObject.value });
}

const options = computed(() => ({
  targetSystems: props.filterOptions?.targetSystems?.map((targetSystem) => ({
    title: targetSystem.name || '',
    value: targetSystem.id,
  })),

  frequencies: props.filterOptions?.recurrences?.map((freq) => ({
    title: t(`FrequencyType.${freq}`),
    value: freq,
  })),

  states: props.filterOptions?.states?.map((state) => ({
    title: t(`StateOfUseEnum.${state}`),
    value: state,
  })),

  approvedBy: props.filterOptions?.reviewsRequired?.map((required) => {
    return required
      ? {
          title: t('ApprovedBy'),
          value: true,
        }
      : {
          title: t('NoApprovalRequired'),
          value: false,
        };
  }),
}));

const { activeTab } = toRefs(props);
const { t } = useTranslation();

const startDateTime = computed(() => toUserTime(filterObject.value.startDateTime));

const endDateTime = computed(() => toUserTime(filterObject.value.endDateTime));

function updateStartTime(time: DateTime | undefined) {
  filterObject.value.startDateTime = time?.startOf('day').setZone('UTC').toISO() ?? undefined;

  updateFilterObject();
}

function updateEndTime(time: DateTime | undefined) {
  filterObject.value.endDateTime = time?.endOf('day').setZone('UTC').toISO() ?? undefined;

  updateFilterObject();
}

function clearFilters() {
  filterObject.value = {};

  updateFilterObject();
}
</script>

<template>
  <v-row class="align-center mt-2 pl-2 pr-2">
    <v-col cols="12" sm="6" md="4" lg="2" class="filter-search-col pa-2">
      <SearchField
        v-model="filterObject.search"
        :placeholder="t('Search')"
        class="flex-grow-1"
        @update:modelValue="updateFilterObject()"
      />
    </v-col>

    <v-col cols="12" sm="6" md="4" lg="2" class="filter-target-system-col pa-2">
      <Autocomplete
        v-model="filterObject.targetSystemIds"
        :label="t('MedicalSystem_other')"
        :items="options.targetSystems"
        multiple
        chips
        closableChips
        emptyAsUndefined
        class="flex-grow-1"
        @update:modelValue="updateFilterObject()"
      />
    </v-col>

    <v-col cols="12" sm="6" md="4" class="filter-recurrence-col pa-2">
      <Autocomplete
        v-model="filterObject.frequencies"
        :label="t('Recurrence')"
        :items="options.frequencies"
        multiple
        chips
        closableChips
        emptyAsUndefined
        class="flex-grow-1"
        @update:modelValue="updateFilterObject()"
      />
    </v-col>

    <v-col cols="12" sm="6" md="4" class="filter-datepicker-col pa-2">
      <v-row noGutters>
        <v-col lg="6">
          <DatePicker
            :modelValue="startDateTime"
            :label="
              activeTab === 'open'
                ? t('JobOccurrencePeriodOfExecution')
                : t('JobOccurrencePlannedPeriodOfExecution')
            "
            class="text-no-wrap"
            style="flex: 1 1 0; min-width: 0"
            @update:modelValue="updateStartTime"
          />
        </v-col>
        <v-col class="align-self-end pl-1" lg="6">
          <DatePicker
            :modelValue="endDateTime"
            class="align-self-end"
            style="flex: 1 1 0; min-width: 0"
            @update:modelValue="updateEndTime"
          />
        </v-col>
      </v-row>
    </v-col>

    <v-col cols="12" sm="6" md="4" class="filter-status-col pa-2">
      <Autocomplete
        v-if="activeTab === 'completed'"
        v-model="filterObject.reviewRequired"
        :label="t('ApprovedBy')"
        :items="options.approvedBy"
        multiple
        chips
        closableChips
        emptyAsUndefined
        class="flex-grow-1"
        @update:modelValue="updateFilterObject()"
      />

      <Autocomplete
        v-else
        v-model="filterObject.states"
        :label="t('StateOfExecution')"
        :items="options.states"
        multiple
        chips
        closableChips
        emptyAsUndefined
        class="flex-grow-1"
        @update:modelValue="updateFilterObject()"
      />
    </v-col>

    <v-col md="3" lg="1" class="d-flex align-center justify-start pa-2 filter-reset-button-col">
      <PtwResetFilterButton @click="clearFilters" />
    </v-col>
  </v-row>
</template>

<style scoped lang="scss">
.text-no-wrap :deep(label) {
  white-space: nowrap !important;
  overflow: visible;
}

.filter-recurrence-col,
.filter-status-col {
  @media (min-width: 1280px) {
    flex: 0 0 12.5%;
    min-width: 180px;
  }
}

.filter-status-col {
  min-width: 200px;
}

.filter-datepicker-col {
  @media (min-width: 1280px) {
    flex: 0 0 25%;
    min-width: 340px;
  }

  // Prevent long DatePicker labels from affecting field width
  :deep(.v-label) {
    max-width: 100%;
    overflow: visible;
    white-space: nowrap;
  }

  :deep(.text-field) {
    min-width: 140px;
    max-width: 100%;
  }

  :deep(.v-col) {
    min-width: 0;
  }
}
</style>
